import React, { useCallback, useEffect, useState } from 'react'
import {
  accordionSummaryStyles,
  cardStyles,
  DragAndDropStack,
  PersonBasicInfoBlock,
  useGlobalMessageHandler,
  useUsLegRoleTypeLuts,
  useLegPoliticalPartiesLuts,
  useUsPrefixLuts
  , MemberCard, MemberStaffCard, PersonSelectDialog, GalleyDialog, type PersonAddress
} from 'components'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Collapse,
  Fab,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  Typography
} from '@mui/material'
import { useSessionContext, MEMBER_NAV_SORT_DEFAULT, type MemberNavSort } from 'session'
import { type PersonBaseBasicInformation, updateStafferDisplayOrder, getUsPersonAddresses, getAvailableOfficeLocations, type MemberLevel } from 'services'
import { useNavigate, useParams } from 'react-router-dom'
import { getMemberNavigation } from 'services/member/memberNavigationService'
import { useRecordNavigation } from 'components/us/common/useRecordNavigation'
import { RecordNavigationButtons } from 'components/us/common/RecordNavigationButtons'
import { RecordNavigationSortMenu, type SortOption } from 'components/us/common/RecordNavigationSortMenu'
import { UnsavedChangesDialog } from 'components/us/common/UnsavedChangesDialog'
import { BulkAssignButton } from 'components/us/bulk-assign/bulk-assign-button'
import { bulkAssignMemberOffice } from 'services/member/memberStaffService'
import { OFFICE_LOCATION } from 'components/us/member/member-staff-card'
import { PHONE_TYPES, US_LEGISLATIVE_MEMBER_ROLES, US_STAFFER_ROLES } from 'utils'
import {
  getMemberInfo,
  getStaffByMember,
  type MemberInformation,
  type MemberRankInformation,
  type MemberStaffInformation
} from 'services/person/us/edit/memberInfoService'
import { getChamberRankings, updateMembersRanking } from 'services/member/memberListService'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AddIcon from '@mui/icons-material/Add'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import { getEmptyLegRole, getEmptyLegSeat, getEmptyPersonAddress, getEmptyStafferEmail, getEmptyPersonPhone } from 'helpers'

const MEMBER_NAV_SORT_OPTIONS: SortOption[] = [
  { value: 'last_name', label: 'Last Name' },
  { value: 'district', label: 'District' }
]

const officeLocationLabel = (id: number): string =>
  OFFICE_LOCATION.get(id)?.name ?? `Office ${id}`

export const MemberUpdatePage: React.FC = () => {
  const { memberId, cachedPersonId } = useParams<{ memberId: string, cachedPersonId: string }>()
  const { sessionData, changeModuleName, changeUniqueId, changeMemberNavSortBy } = useSessionContext()
  const parsedMemberId = memberId != null ? parseInt(memberId, 10) : 1
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const navSortBy: MemberNavSort = sessionData.memberNavSortBy ?? MEMBER_NAV_SORT_DEFAULT
  const handleNavSortChange = useCallback((next: string): void => {
    changeMemberNavSortBy(next as MemberNavSort)
  }, [changeMemberNavSortBy])

  const fetchMemberNav = useCallback(
    async (id: number) => await getMemberNavigation(id, navSortBy),
    [navSortBy]
  )

  const { previousId, nextId, loading: navLoading } = useRecordNavigation(
    fetchMemberNav,
    parsedMemberId
  )
  const [listStaffersByOfficeLocation, setListStaffersByOfficeLocation] = useState(true)
  const [personId, setPersonId] = useState(0)
  const { showErrorMessage } = useGlobalMessageHandler()
  const [expanded, setExpanded] = useState(true)
  const [displaySortedByDistrict, setDisplaySortedByDistrict] = useState<'block' | 'none'>('none')
  const [displaySortedByOrder, setDisplaySortedByOrder] = useState<'block' | 'none'>('none')

  const [reloadTrigger, setReloadTrigger] = useState(0)
  const [openStaffDialog, setOpenStaffDialog] = useState(false)
  const [openGalleyDialog, setOpenGalleyDialog] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [pendingNavigationId, setPendingNavigationId] = useState<number | null>(null)
  const [unsavedChangesOpen, setUnsavedChangesOpen] = useState(false)
  const [saveTrigger, setSaveTrigger] = useState(0)
  const [memberAddresses, setMemberAddresses] = useState<PersonAddress[]>([])
  const [availableOfficeLocations, setAvailableOfficeLocations] = useState<number[]>([])
  const [collapsedOffices, setCollapsedOffices] = useState<Set<number>>(new Set())

  const toggleOfficeCollapse = (officeLocationId: number): void => {
    setCollapsedOffices((prev) => {
      const next = new Set(prev)
      if (next.has(officeLocationId)) next.delete(officeLocationId)
      else next.add(officeLocationId)
      return next
    })
  }

  useEffect(() => {
    setDisplaySortedByDistrict(listStaffersByOfficeLocation ? 'block' : 'none')
    setDisplaySortedByOrder(listStaffersByOfficeLocation ? 'none' : 'block')
  }, [listStaffersByOfficeLocation])

  const [position, setPosition] = useState<MemberInformation>({
    id: 0,
    person: {
      id: 0,
      lastName: ''
    },
    legrole: getEmptyLegRole(),
    legseat: getEmptyLegSeat()
  })
  const [personBasicInfo, setPersonBasicInfo] = useState <PersonBaseBasicInformation>({
    id: parseInt(cachedPersonId ?? '', 10),
    lastName: ''
  })
  const [staff, setStaff] = useState<MemberStaffInformation[]>([])
  const [creatingStaff, setCreatingStaff] = useState<MemberStaffInformation | undefined>(undefined)
  const { lutValues: legRoleTypeLutValues, loading: loadingLegRoleTypeLutValues } = useUsLegRoleTypeLuts()
  const { lutValues: politicalPartyLutValues } = useLegPoliticalPartiesLuts()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()

  const updateBasicInfo = (newData: PersonBaseBasicInformation): void => {
    setPersonBasicInfo(newData)
  }

  useEffect(() => {
    changeModuleName('Member')
  }, [changeModuleName])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [parsedMemberId])

  useEffect(() => {
    if (loading) {
      changeUniqueId('')
      document.title = 'Member'
      return
    }

    const districtCode = position.legseat?.legdistrict?.code ?? ''
    const partyCode = politicalPartyLutValues?.parties?.find(party => party.id === position.person?.partyId)?.code ?? ''
    const prefix = usPrefixLutValues.prefixes.find(option => option.id === personBasicInfo.prefixId)?.code ?? ''
    const fullName = `${prefix} ${personBasicInfo.firstName ?? ''} ${personBasicInfo.middleName ?? ''} ${personBasicInfo.lastName ?? ''}`.trim()
    const parenthesesContent = []

    if (districtCode !== '') {
      const cleanDistrictCode = districtCode.replace('-', '')
      parenthesesContent.push(cleanDistrictCode)
    }

    if (partyCode !== '') {
      parenthesesContent.push(partyCode)
    }

    let title = `${memberId ?? ''} - ${fullName}`

    if (parenthesesContent.length > 0) {
      title += ` (${parenthesesContent.join(' ')})`
    }

    changeUniqueId(title)
    document.title = `Member ${memberId ?? ''} - ${personBasicInfo.firstName ?? ''} ${personBasicInfo.lastName ?? ''} (${personId ?? ''})`
  }, [personBasicInfo, position, politicalPartyLutValues, usPrefixLutValues.prefixes, memberId, personId, loading])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true)
        const [memberInfoResponse, memberStaffInfoResponse] = await Promise.all([getMemberInfo(parsedMemberId), getStaffByMember(parsedMemberId)])
        setPosition(memberInfoResponse)
        setPersonId(memberInfoResponse.person.id)
        setPersonBasicInfo(memberInfoResponse.person)
        setStaff(memberStaffInfoResponse)
        navigate(`/us/editMember/${parsedMemberId}/${memberInfoResponse.person.id}`, { replace: true })

        if (memberInfoResponse.person?.id != null && memberInfoResponse.person.id !== 0) {
          try {
            const addresses = await getUsPersonAddresses(memberInfoResponse.person.id)
            setMemberAddresses(addresses)
            const memberLevel: MemberLevel = memberInfoResponse.legrole.role.id === US_LEGISLATIVE_MEMBER_ROLES.FED_LEGISLATIVE_MEMBER ? 'federal' : 'state'
            const available = getAvailableOfficeLocations(addresses, memberLevel)
            setAvailableOfficeLocations(available)
          } catch (addressError) {
            console.error('Error loading member addresses:', addressError)
          }
        }

        const staffByLocation = new Map<number, MemberStaffInformation[]>()
        for (let index = 0; index < memberStaffInfoResponse.length; index++) {
          const locationId = memberStaffInfoResponse[index].legmemOfficeLocationId
          if (!staffByLocation.has(locationId)) {
            staffByLocation.set(locationId, [])
          }
          staffByLocation.get(locationId)?.push(memberStaffInfoResponse[index])
        }
      } catch (error: any) {
        let errorMessage = 'An unexpected error occurred during the request.'
        if (error.response?.status === 404 || error.response?.status === 400) {
          errorMessage = Object.values(error.response.data.errors).join('\n')
        }
        showErrorMessage(errorMessage)
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
  }, [parsedMemberId])

  const addStaffer = (): void => {
    setOpenStaffDialog(true)
  }

  const getStafferRoleForMember = (memberRole: number): number => {
    return memberRole === US_LEGISLATIVE_MEMBER_ROLES.FED_LEGISLATIVE_MEMBER
      ? US_STAFFER_ROLES.FEDERAL_MEMBER_STAFF
      : US_STAFFER_ROLES.STATE_MEMBER_STAFF
  }

  const handlePersonSelected = (person: PersonBaseBasicInformation): void => {
    setOpenStaffDialog(false)
    const emptyAddress = getEmptyPersonAddress(person.id ?? 0)

    const defaultPhone = getEmptyPersonPhone(person.id ?? 0, emptyAddress.id)
    defaultPhone.typeId = PHONE_TYPES.PHONE

    const defaultFax = getEmptyPersonPhone(person.id ?? 0, emptyAddress.id)
    defaultFax.typeId = PHONE_TYPES.FAX

    emptyAddress.phones = [defaultPhone, defaultFax]

    const newStaffer: MemberStaffInformation = {
      id: 0,
      person,
      role: {
        id: getStafferRoleForMember(position.legrole.role.id),
        desc: '',
        code: '',
        branch: { id: 2, branch_desc: '' }
      },
      legrole: undefined,
      legrole2: undefined,
      legrole3: undefined,
      legmem: position,
      legmemOfficeLocationId: 1,
      statusId: undefined,
      title: undefined,
      email: getEmptyStafferEmail(person.id ?? 0),
      address: emptyAddress,
      displayOrder: 0,
      updatedBy: undefined,
      lastUpdated: undefined,
      responsibilities: []
    }
    setCreatingStaff(newStaffer)
  }

  const handleAccordionChange = (event: React.ChangeEvent<any>, isExpanded: boolean): void => {
    if ((event.target as HTMLElement).closest('.MuiSwitch-root') == null) {
      setExpanded(isExpanded)
    }
  }

  const handleStaffRetire = (retiredStaff: MemberStaffInformation): void => {
    setStaff(prevStaff => prevStaff.filter(staff => staff.id !== retiredStaff.id))
    setReloadTrigger(prev => prev + 1)
  }

  const refreshStaff = useCallback(async (): Promise<void> => {
    try {
      const refreshed = await getStaffByMember(parsedMemberId)
      setStaff(refreshed)
      setReloadTrigger(prev => prev + 1)
    } catch (error) {
      showErrorMessage('Failed to refresh staffers after bulk assign.')
    }
  }, [parsedMemberId, showErrorMessage])

  const handleBulkAssignSuccess = useCallback(async (): Promise<void> => {
    await refreshStaff()
  }, [refreshStaff])

  const renderStafferCardsByOfficeLocation = (): React.ReactNode => {
    if (staff == null) return null

    const byOffice = new Map<number, MemberStaffInformation[]>()
    for (const staffer of staff) {
      const locationId = staffer.legmemOfficeLocationId ?? 0
      if (!byOffice.has(locationId)) byOffice.set(locationId, [])
      byOffice.get(locationId)?.push(staffer)
    }
    const sortedOffices = Array.from(byOffice.entries()).sort(([a], [b]) => a - b)
    const memberDisplayName = `${personBasicInfo.firstName ?? ''} ${personBasicInfo.lastName ?? ''}`.trim()

    return (
      <Stack spacing={3}>
        {sortedOffices.map(([officeLocationId, stafferList]) => {
          const sortedStaffers = [...stafferList].sort((a, b) => {
            const roleOrderComparison = (a.legrole?.lutOrder ?? 0) - (b.legrole?.lutOrder ?? 0)
            if (roleOrderComparison !== 0) return roleOrderComparison
            return (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
          })
          const label = officeLocationLabel(officeLocationId)
          const hasSourceAddress = availableOfficeLocations.includes(officeLocationId)
          const isExpanded = !collapsedOffices.has(officeLocationId)
          return (
            <Box key={officeLocationId}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                    <IconButton
                      size="small"
                      aria-label={isExpanded ? `Collapse ${label}` : `Expand ${label}`}
                      aria-expanded={isExpanded}
                      onClick={() => { toggleOfficeCollapse(officeLocationId) }}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Tooltip>
                  <Typography variant="subtitle1">{`${label} (${sortedStaffers.length})`}</Typography>
                </Box>
                <BulkAssignButton
                  ownerDisplayName={memberDisplayName}
                  targetLabel={label}
                  buttonLabel={`Apply ${label} info to all staffers`}
                  dialogTitle={`Bulk-assign ${label} info`}
                  staffers={sortedStaffers}
                  hasSourceAddress={hasSourceAddress}
                  onConfirm={async () => await bulkAssignMemberOffice(parsedMemberId, officeLocationId)}
                  onSuccess={handleBulkAssignSuccess}
                />
              </Box>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Stack spacing={2}>
                  {sortedStaffers.map((staffer) => (
                    <MemberStaffCard
                      key={staffer.id}
                      createCard={false}
                      originalPosition={staffer}
                      legRoleLuts={legRoleTypeLutValues}
                      loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                      onStaffUpdate={handleStaffUpdate}
                      onRetire={handleStaffRetire}
                      memberAddresses={memberAddresses}
                      availableOfficeLocations={availableOfficeLocations}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Box>
          )
        })}
      </Stack>
    )
  }

  const renderStafferCardsByType = (): React.ReactNode => {
    if (staff == null) return null

    const sortedStaff = staff.sort((a, b) => {
      const lutOrderA = a.legrole?.lutOrder ?? 0
      const lutOrderB = b.legrole?.lutOrder ?? 0

      if (lutOrderA !== lutOrderB) {
        return lutOrderA - lutOrderB
      }

      const displayOrderA = a.displayOrder ?? 0
      const displayOrderB = b.displayOrder ?? 0
      return displayOrderA - displayOrderB
    })

    const staffersByRoleMap = sortedStaff.reduce((map, staffer) => {
      const roleId = staffer.legrole?.id ?? 0
      if (map.get(roleId) == null) {
        map.set(roleId, [])
      }
      map.get(roleId)?.push(staffer)
      return map
    }, new Map<number, MemberStaffInformation[]>())

    const sortedStaffersByRole = Array.from(staffersByRoleMap.entries())
      .sort(([_, staffersA], [__, staffersB]) => {
        const lutOrderA = staffersA[0]?.legrole?.lutOrder ?? 0
        const lutOrderB = staffersB[0]?.legrole?.lutOrder ?? 0
        return lutOrderA - lutOrderB
      })

    const groupedStaffList = (
      <Stack spacing={2}>
        {sortedStaffersByRole.flatMap(([roleId, staffers]) => (
          <DragAndDropStack
            key={roleId}
            id={`staff-${memberId ?? ''}-${roleId}`}
            originalItems={staffers}
            itemRenderer={(item) => (
              <MemberStaffCard
                key={item.id as number}
                createCard={false}
                originalPosition={item}
                legRoleLuts={legRoleTypeLutValues}
                loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                onStaffUpdate={handleStaffUpdate}
                onRetire={handleStaffRetire}
                memberAddresses={memberAddresses}
                availableOfficeLocations={availableOfficeLocations}
              />
            )}
            updateDisplayOrder={async (items, itemSelected) => {
              const displayOrderById = items.reduce<Record<number, number>>((acc, staff, index) => ({ ...acc, [staff.id]: index + 1 }), {})
              const response = await updateStafferDisplayOrder(parsedMemberId, displayOrderById, itemSelected.id, itemSelected.legrole.id)
              if (response.succeeded) {
                setStaff(prevStaff =>
                  [...prevStaff.map(staff =>
                    displayOrderById[staff.id] != null ? { ...staff, displayOrder: displayOrderById[staff.id] } : staff
                  )]
                )
              }
              return response
            }}
            disableDrag={loading || staff.length <= 1}
            makeGray={false}
          />
        ))}
      </Stack>
    )

    return groupedStaffList
  }

  const handleStaffUpdate = (updatedStaff: MemberStaffInformation): void => {
    setStaff(prevStaff =>
      prevStaff.map(staff => staff.id === updatedStaff.id ? updatedStaff : staff)
    )
    setReloadTrigger(prev => prev + 1)
  }

  const memoizedStafferCardsByDistrict = React.useMemo(
    () => renderStafferCardsByOfficeLocation(),
    [staff, memberId, reloadTrigger, memberAddresses, availableOfficeLocations, personBasicInfo.firstName, personBasicInfo.lastName, collapsedOffices]
  )

  const memoizedStafferCardsByType = React.useMemo(
    () => renderStafferCardsByType(),
    [staff, memberId, reloadTrigger, memberAddresses, availableOfficeLocations]
  )

  const handleRankingUpdated = async (updatedMembers: MemberRankInformation[]): Promise<void> => {
    if (updatedMembers.length > 0) {
      const result = await updateMembersRanking(updatedMembers)
      if (result.succeeded) {
        void refreshMemberPage()
      } else {
        showErrorMessage('Failed to update member rankings')
      }
    }
  }

  const redirectToMemberPage = (personId: number): void => {
    window.location.href = `/us/editMember/${parsedMemberId}/${personId}`
  }

  const clearPersonState = useCallback((): void => {
    setPersonId(0)
    setPersonBasicInfo({ id: 0, lastName: '' })
    setSaveTrigger(0)
  }, [setPersonId, setPersonBasicInfo, setSaveTrigger])

  const handleNavigateToRecord = (id: number): void => {
    if (isDirty) {
      setPendingNavigationId(id)
      setUnsavedChangesOpen(true)
      return
    }
    clearPersonState()
    navigate(`/us/editMember/${id}/0`)
  }

  const handleSaveAndNavigate = (): void => {
    setSaveTrigger((prev) => prev + 1)
    setUnsavedChangesOpen(false)
  }

  const handleDiscardAndNavigate = (): void => {
    setIsDirty(false)
    setUnsavedChangesOpen(false)
    if (pendingNavigationId != null) {
      clearPersonState()
      navigate(`/us/editMember/${pendingNavigationId}/0`)
      setPendingNavigationId(null)
    }
  }

  const handleCancelNavigation = (): void => {
    setUnsavedChangesOpen(false)
    setPendingNavigationId(null)
  }

  useEffect(() => {
    if (!isDirty && pendingNavigationId != null) {
      const targetId = pendingNavigationId
      setPendingNavigationId(null)
      clearPersonState()
      navigate(`/us/editMember/${targetId}/0`)
    }
  }, [isDirty, pendingNavigationId, navigate, clearPersonState])

  const refreshMemberPage = async (): Promise<void> => {
    try {
      setLoading(true)
      const memberInfoResponse = await getMemberInfo(parsedMemberId)
      setPosition(memberInfoResponse)
      setPersonBasicInfo(memberInfoResponse.person)
    } catch (error: any) {
      let errorMessage = 'An unexpected error occurred during the request.'
      if (error.response?.status === 404 || error.response?.status === 400) {
        errorMessage = Object.values(error.response.data.errors).join('\n')
      }
      showErrorMessage(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleFetchChamberRankings = async (): Promise<MemberRankInformation[]> => {
    if (position?.legseat?.legdistrict?.legchamber?.id == null || position.legseat.legdistrict.legchamber.id === 0) {
      return []
    }

    const chamberId = position.legseat.legdistrict.legchamber.id
    const stateId = position.legseat.legdistrict.state?.id

    try {
      const response = await getChamberRankings(
        chamberId,
        [3, 4].includes(chamberId) ? stateId : undefined
      )
      return response.data
    } catch (error) {
      showErrorMessage('Failed to fetch chamber members')
      return []
    }
  }

  const addToExistingStaffers = (newStaffer: MemberStaffInformation): void => {
    setStaff(prevStaff => [...prevStaff, newStaffer])
    setReloadTrigger(prev => prev + 1)
  }

  const isVacant = (): boolean => position.person?.lastName === 'Vacant'

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 1
        }}
      >
        <Stack spacing={2} alignItems="center" mt={1} width="98%">
          {!isVacant() && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RecordNavigationButtons
                  previousId={previousId}
                  nextId={nextId}
                  onNavigate={handleNavigateToRecord}
                  loading={loading || navLoading}
                />
                <RecordNavigationSortMenu
                  options={MEMBER_NAV_SORT_OPTIONS}
                  value={navSortBy}
                  onChange={handleNavSortChange}
                  disabled={loading || navLoading}
                />
              </Box>
              <Tooltip title="Generate Proofing Galley">
                <IconButton
                  color="primary"
                  onClick={() => { setOpenGalleyDialog(true) }}
                  disabled={loading}
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {!isVacant() &&
          <PersonBasicInfoBlock
            originalBasicInfo={personBasicInfo}
            loading={loading}
            updateBasicInfo={updateBasicInfo}
            onDirtyChange={setIsDirty}
            saveTrigger={saveTrigger}
          />
          }
          {!loading && (
            <MemberCard
              index={0}
              originalPosition={position}
              person={personBasicInfo}
              loading={loading}
              createCard={false}
              onRankingUpdated={handleRankingUpdated}
              onFetchChamberRankings={handleFetchChamberRankings}
              staff={staff}
              redirectToMemberPage={redirectToMemberPage}
              onDirtyChange={setIsDirty}
              saveTrigger={saveTrigger}
            />
          )}
          <Card sx={cardStyles}>
            <Accordion expanded={expanded} onChange={handleAccordionChange}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" sx={accordionSummaryStyles}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="h6">Staffers</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={listStaffersByOfficeLocation}
                        onChange={() => { setListStaffersByOfficeLocation(!listStaffersByOfficeLocation) }}
                        color="primary"
                      />
                    }
                    label={`Order By ${listStaffersByOfficeLocation ? 'Location' : 'Role Type'}`}
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <CardContent>
                {!loading &&
                  <>
                  {creatingStaff == null &&
                    <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                      <Fab color="primary" aria-label="add" onClick={() => { addStaffer() } }>
                        <AddIcon />
                      </Fab>
                    </Box>
                  }
                  <Box display={displaySortedByDistrict}>
                  {memoizedStafferCardsByDistrict}
                  </Box>
                  <Box display={displaySortedByOrder}>
                  {memoizedStafferCardsByType}
                  </Box>
                    {creatingStaff != null && (
                      <Box mt={2}>
                        <MemberStaffCard<MemberStaffInformation>
                          key="new-staff-card"
                          createCard={true}
                          originalPosition={creatingStaff}
                          legRoleLuts={legRoleTypeLutValues}
                          loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                          onStaffUpdate={setCreatingStaff}
                          cancelCreate={() => { setCreatingStaff(undefined) }}
                          addToExistingStaffers={(createdStaff) => { addToExistingStaffers(createdStaff) }}
                          memberAddresses={memberAddresses}
                          availableOfficeLocations={availableOfficeLocations}
                        />
                      </Box>
                    )}
                  </>
                }
                </CardContent>
              </AccordionDetails>
            </Accordion>
          </Card>
        </Stack>
      </Box>
      <UnsavedChangesDialog
        open={unsavedChangesOpen}
        onSave={handleSaveAndNavigate}
        onDiscard={handleDiscardAndNavigate}
        onCancel={handleCancelNavigation}
      />
      <PersonSelectDialog<PersonBaseBasicInformation>
        open={openStaffDialog}
        onClose={() => { setOpenStaffDialog(false) }}
        onPersonSelected={handlePersonSelected}
        title="Select Person"
        personType="personUS"
        fullWidth={true}
        maxWidth="md"
        minHeight="60vh"
      />
      <GalleyDialog
        open={openGalleyDialog}
        onClose={() => { setOpenGalleyDialog(false) }}
        memberId={parsedMemberId}
      />
    </>
  )
}
